import { urls } from '../constants'
import type { ActivityStatus } from '../constants'
import { isAPIError, put } from '@user/store/services/crud'
import { toast } from '@sales-center/components'
import { updateActivityStatusActionCreator } from '@user/features/Activity/state'

type Props = {
  id: number
  status: ActivityStatus
}

export type ActivityStatusWrite =
  | { ok: true; completedDate?: string | null }
  | { ok: false }

/**
 * The single writer for an activity's status: owns the request, the response
 * parsing and the failure toast, so every caller reports failures identically.
 *
 * Callers that live inside an ActivitiesContext should use
 * `updateActivityStatusEffect` below, which also produces the state action. The
 * Unified Calendar's detail popover (SH-1986) has no such context — the action
 * is provider-local and also resets that panel's navigation — so it calls this
 * directly and reacts to the result itself.
 */
export async function writeActivityStatus(
  props: Props,
): Promise<ActivityStatusWrite> {
  const response = await put(urls.updateStatus, props)
  const result = await response.json()

  if (!response.ok) {
    // The API error envelope is flat — { StatusCode, Message } straight from
    // ExceptionMiddleware — so there is no `payload` to read. Reading one meant
    // isAPIError() dereferenced undefined and threw, so this never actually
    // returned a reported failure and the server's reason was never shown.
    toast.error(
      isAPIError(result) ? result.Message : 'Failed to update Activity status',
    )
    return { ok: false }
  }

  return { ok: true, completedDate: result.completedDate }
}

export async function updateActivityStatusEffect(props: Props) {
  const result = await writeActivityStatus(props)

  if (!result.ok) return updateActivityStatusActionCreator()

  return updateActivityStatusActionCreator({
    ...props,
    completedDate: result.completedDate,
  })
}
